import { fromUint8Array } from 'js-base64'
import { gzip } from 'pako'

import { kqlEscape } from '../escape-utils'
import { FieldMapping } from '../interfaces'

import { Tool } from './base'
import {
  etaFieldLookup,
  makeRenderer,
  normalizeList,
  PipeAndGroups,
  RenderContext,
  scalarToString,
} from './render/render-kit'
import { DataDisplayTemplate } from './render/rtd/datadisplay.types'
import { wrapRendererWithDataDisplay } from './render/rtd/datadisplay.utils'
import { NormalizedDataSource } from './render/rtd/datasource.types'
import {
  joinWithPipeBlock,
  wrapRendererWithDatasource,
} from './render/rtd/datasource.utils'
import { sentinelMap } from './render/rtd/tool-datasource-mappings/microsoft-sentinel.datasource'
import { AstPredicate, ValueOrList } from './render/types'

// Sentinel uses Eta-wrapped field names ({{!=field!}}) since its tuning renderer wraps fields.
// This template applies Eta wrapping to all field refs in projection and aggregation clauses.
const eta = (f: string) => `{{!=${f}!}}`

export const sentinelKqlDataDisplayTemplate: DataDisplayTemplate = {
  projection: (fields) => `| project ${fields.map(eta).join(', ')}`,

  groupBy: (groupFields, aggClauses) => {
    const allClauses = [
      'firstEvent=min(TimeGenerated)',
      'lastEvent=max(TimeGenerated)',
      ...aggClauses,
      'Count=count()',
    ]
    return `| summarize ${allClauses.join(', ')} by ${groupFields.map(eta).join(', ')}\n| order by firstEvent asc`
  },

  timeBucket: (span, groupFields, aggClauses) => {
    const allClauses = [
      'firstEvent=min(TimeGenerated)',
      'lastEvent=max(TimeGenerated)',
      ...aggClauses,
      'Count=count()',
    ]
    const byFields = [
      ...groupFields.map(eta),
      `bin(TimeGenerated, ${span})`,
    ].join(', ')
    return `| summarize ${allClauses.join(', ')} by ${byFields}\n| order by firstEvent asc`
  },

  aggFns: {
    count: (_field, alias) => `${alias}=count()`,
    dcount: (field, alias) => `${alias}=dcount(${eta(field)})`,
    values: (field, alias) => `${alias}=make_set(${eta(field)})`,
    collect: (field, alias) => `${alias}=make_set(${eta(field)})`,
    first: (field, alias) => `${alias}=arg_min(Timestamp, ${eta(field)})`,
    last: (field, alias) => `${alias}=arg_max(Timestamp, ${eta(field)})`,
    sum: (field, alias) => `${alias}=sum(${eta(field)})`,
    avg: (field, alias) => `${alias}=avg(${eta(field)})`,
    min: (field, alias) => `${alias}=min(${eta(field)})`,
    max: (field, alias) => `${alias}=max(${eta(field)})`,
  },

  fieldAlias: (field, alias) => `${alias}=${eta(field)}`,

  join: (body, ddClause) => `${body}\n${ddClause}`,
}

export { sentinelMap }

export class MicrosoftSentinelTool extends Tool {
  public readonly toolName = 'Microsoft Sentinel'
  public readonly toolKind = 'siem' as const
  public override readonly customMapping = true
  public override readonly toolSpecific = ['tableName']
  public override readonly allowedCategories = ['endpoint:wineventlog']
  public override readonly datasourceMap = sentinelMap

  public override supportedLogCategories(): string[] {
    return ['endpoint:wineventlog']
  }
  public readonly dataDisplayTemplate = sentinelKqlDataDisplayTemplate

  // Temporary until a better global default for Sentinel can be built
  public defaultFieldMapping: FieldMapping = {
    name: 'foo',
    category: 'foo',
    fields: [],
    toolSpecific: [{ tableName: '*' }],
  }

  public readonly toolDefaultCCMFieldMappings = {
    //src_ip: '',
    //application_name: '',
    //parent_process_cmdline: '',
    access: 'AccessMask',
    action: 'Activity',
    application_name: 'Application',
    dest_domain: 'DestAddress',
    logon_process: 'LogonProcessName',
    object_loaded: 'ObjectName',
    protocol: 'Protocol',
    record_id: 'Task',
    sid: 'SubjectUserSid',
    src_hostname: 'Computer',
    src_ip: 'IpAddress',
    src_port: 'IpPort',
    task_category: 'TaskCategory',
    accesses: 'AccessList',
    dest_ip: 'RemoteIpAddress',
    dest_port: 'RemotePort',
    event_id: 'EventID',
    event_type: 'EventTypeName',
    file_name: 'ObjectName',
    file_path: 'ObjectName',
    hostname: 'Computer',
    logon_type: 'LogonType',
    message: 'EventData',
    object_name: 'ObjectName',
    parent_process_id: 'ProcessId',
    parent_process_name: 'ParentProcessName',
    parent_process_path: 'ParentProcessName',
    process_cmdline: 'CommandLine',
    process_id: 'NewProcessId',
    process_name: 'Process',
    process_path: 'NewProcessName',
    registry_key_name: 'ObjectValueName',
    registry_new_value_name: 'NewValue',
    registry_old_value_name: 'OldValue',
    registry_path: 'ObjectName',
    registry_value_name: 'NewValue',
    service_file_path: 'ServiceFileName',
    service_name: 'ServiceName',
    target_path: 'ObjectName',
    username: 'SubjectUserName',
    user_agent: 'UserAgent',
    region: 'AWSRegion',
    event_name: 'EventName',
    user_type: 'UserIdentityType',
    group_id: 'UserIdentityAccountId',
    identity_principal_id: 'UserIdentityPrincipalid',
    bytes_in: 'parse_json(AdditionalEventData).bytesTransferredOIn',
    bytes_out: 'parse_json(AdditionalEventData).bytesTransferredOIn',
    account_id: 'UserIdentityAccountId',
    error_code: 'ErrorCode',
    error_message: 'ErrorMessage',
    dest_hostname: 'parse_json(RequestParameters).Host',
    storage_property_name: 'parse_json(RequestParameters).bucketName',
    storage_target_object: 'parse_json(RequestParameters).key',
    user_access_id: 'UserIdentityAccessKeyId',
    source_user_role: 'parse_json(RequestParameters).roleArn',
    user_role: 'UserIdentityArn',
    mfa_auth: 'SessionMfaAuthenticated',
    cloud_event_id: 'AwsEventId',
    request_parameters: 'RequestParameters',
  }

  public generateSearchPermalink(
    rootURL: string,
    renderedContent: string,
  ): string {
    const encodedRenderedContent = encodeURIComponent(
      fromUint8Array(gzip(renderedContent)),
    )
    return `${rootURL}source/LogsBlade.AnalyticsShareLinkToQuery/q/${encodedRenderedContent}/timespan/P1D`
  }

  public pivotEscape(key: string, value: string): string {
    return kqlEscape(value)
  }

  public renderTuning = makeRenderer({
    tool: this.toolName,
    groupStrategy: PipeAndGroups,
    renderContext: {
      linePrefix: '| where ',
      andGlue: ' and ',
      orGlue: ' or ',
      wrapField: (f) => etaFieldLookup(f), // safe Eta key-lookup (SSTI-hardened)
      // quote(): KQL string literal with escaping
      quote: (s: string) =>
        `"${String(s).replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`,
      // list() not used directly; we build per-item with smart quoting in LIST() above
    },
    formatPredicate(p: AstPredicate, renderContext: RenderContext): string {
      const f = renderContext.wrapField(renderContext.mapField(p.field))

      // Raw list preserves original types so we can avoid quoting numbers
      const raw: ValueOrList | undefined = p.value
      const rawList =
        raw == null ? [undefined] : Array.isArray(raw) ? raw : [raw]

      // String list (for shaping/escaping where needed)
      const vals = normalizeList(p.value).map(scalarToString)

      // Quote smartly: numbers → bare; everything else → renderContext.quote
      const Q = (s: string, rawVal: any) =>
        typeof rawVal === 'number' ? String(rawVal) : renderContext.quote(s)

      const LIST = () => {
        const items = vals.map((s, i) => Q(s, rawList[i]))
        return `(${items.join(', ')})`
      }

      // Helpers
      const isMulti = vals.length > 1

      switch (p.op) {
        // Null / Empty (KQL built-ins)
        case 'isnull':
          return `isnull(${f})`
        case 'isnotnull':
          return `isnotnull(${f})`
        case 'isempty':
          return `isempty(${f})`
        case 'isnotempty':
          return `isnotempty(${f})`

        // Equality / inequality (prefer case-insensitive =~ / in~)
        case 'eq':
          return isMulti
            ? `${f} in~ ${LIST()}`
            : `${f} =~ ${Q(vals[0], rawList[0])}`
        case 'neq':
          return isMulti
            ? `${f} !in~ ${LIST()}`
            : `${f} !~ ${Q(vals[0], rawList[0])}`

        // Token-oriented contains (faster than substring)
        case 'contains':
          return isMulti
            ? `${f} has_any ${LIST()}`
            : `${f} has ${Q(vals[0], rawList[0])}`
        case 'doesnotcontain':
          return isMulti
            ? `not(${f} has_any ${LIST()})`
            : `${f} !has ${Q(vals[0], rawList[0])}`

        // Starts with and ends with
        case 'startswith':
          return isMulti
            ? `(${vals.map((s, i) => `${f} startswith ${Q(s, rawList[i])}`).join(renderContext.orGlue)})`
            : `${f} startswith ${Q(vals[0], rawList[0])}`
        case 'endswith':
          return isMulti
            ? `(${vals.map((s, i) => `${f} endswith ${Q(s, rawList[i])}`).join(renderContext.orGlue)})`
            : `${f} endswith ${Q(vals[0], rawList[0])}`

        // Comparisons (use smart quoting)
        case 'lt':
          return `${f} < ${Q(vals[0], rawList[0])}`
        case 'lte':
          return `${f} <= ${Q(vals[0], rawList[0])}`
        case 'gt':
          return `${f} > ${Q(vals[0], rawList[0])}`
        case 'gte':
          return `${f} >= ${Q(vals[0], rawList[0])}`

        default:
          // Safe default → equality
          return isMulti
            ? `${f} in~ ${LIST()}`
            : `${f} =~ ${Q(vals[0], rawList[0])}`
      }
    },
  })

  public readonly tuningText =
    "Insert tuning logic after the last line of base logic (ex. | where FileName has \"example\"). This will typically be right before the '| project ...' or '| summarize ...' functions."

  /**
   * Emit the KQL table name followed by an optional EventID filter.
   * Windows Event Log categories always carry EventCodes so the query is
   * scoped to a specific EventID (e.g. 4688 for process creation).
   */
  public emitSentinelDatasource(ds: NormalizedDataSource | undefined): string {
    if (!ds) return ''
    const repos = ds.repos ?? (ds.repo ? [ds.repo] : [])
    if (repos.length === 0) return ''
    const table = repos.length === 1 ? repos[0] : `union ${repos.join(', ')}`
    const codes = ds.eventCodes ?? []
    if (!codes.length) return table
    const filter =
      codes.length === 1
        ? `| where EventID == ${codes[0]}`
        : `| where EventID in (${codes.join(', ')})`
    return `${table}\n${filter}`
  }

  /** Merge the base logic (tuning) with datasource table routing and DataDisplay clause. */
  public renderMicrosoftSentinel = wrapRendererWithDataDisplay(
    wrapRendererWithDatasource(
      this.renderTuning,
      this.toolName,
      (ds) => this.emitSentinelDatasource(ds),
      joinWithPipeBlock,
      this.tuningText,
    ),
    this.toolName,
    sentinelKqlDataDisplayTemplate,
  )
}
